import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import HiddenFollowUpsBadge from "../../components/HiddenFollowUpsBadge/HiddenFollowUpsBadge";
import FieldSelect from "../../components/forms/FieldSelect";
import { editorScopeLabel, scopeAppliesTo } from "../../utils/helpers/standardScope";
import {
  computeQuestionDepths,
  computeVisibleKeys,
  displayText,
  findDefaultOptionSet,
  optionSetsByUuid,
} from "./templateTree";

// Interactive preview of a template: renders standards/questions exactly like
// the live observation form (domain card, progress strip, option pills) and
// lets the author click answers to try the visibility conditions. Gating is
// evaluated as a full graph (fixpoint), matching the live form. Purely local
// state — nothing is submitted.
// `lang` is the editor's AUTHORING language, not the UI language: the point of
// previewing while translating is to see the rubric as a reader of that
// language will get it, fallback chain and all.
// A rubric with subject-scoped standards adds a "show for subject" picker that
// narrows the preview to what an observer of that lesson gets.
export default function TemplatePreview({ standards: allStandards, optionSets, subjectCatalog, lang }) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState({});
  const [subject, setSubject] = useState(""); // "" = every standard
  const byUuid = optionSetsByUuid(optionSets);
  const defaultSet = findDefaultOptionSet(optionSets);
  const isScoped = allStandards.some((std) => std.subjectScope !== "all");

  // Numbered by their place in the rubric, not in the filtered list: a maths
  // lesson that skips standard 3 still calls the next one 4, as reports do.
  const standards = useMemo(
    () =>
      allStandards
        .map((std, index) => ({ ...std, number: index + 1 }))
        .filter((std) => !subject || scopeAppliesTo(std.subjectScope, std.subjects, subject)),
    [allStandards, subject],
  );

  const visibleKeys = useMemo(
    () => computeVisibleKeys(standards, answers),
    [standards, answers],
  );

  const depthsByStd = useMemo(() => {
    const map = {};
    standards.forEach((std, idx) => {
      map[std.key || idx] = computeQuestionDepths(std);
    });
    return map;
  }, [standards]);

  // gate question -> questions that depend on it (any standard); used to
  // advertise hidden follow-ups so gated questions are never "lost".
  const dependentsByKey = useMemo(() => {
    const map = {};
    standards.forEach((std) =>
      std.questions.forEach((q) =>
        (q.conditions || []).forEach((c) => {
          (map[c.dependsOnKey] = map[c.dependsOnKey] || []).push(q.key);
        }),
      ),
    );
    return map;
  }, [standards]);

  const optionsFor = (question) =>
    (byUuid[question.optionSet] || defaultSet)?.options || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-700">
        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="flex-1 text-xs font-semibold">{t("templates.previewHint")}</p>
        {isScoped && (
          <div className="w-56 shrink-0">
            <FieldSelect
              value={subject}
              onChange={setSubject}
              options={[
                { value: "", label: t("subjectScope.anySubject") },
                ...subjectCatalog.options,
              ]}
              searchable
              searchPlaceholder={t("common.search")}
              ariaLabel={t("subjectScope.previewFor")}
            />
          </div>
        )}
      </div>

      {standards.map((std, stdIdx) => {
        const depths = depthsByStd[std.key || stdIdx];
        const scopeLabel = editorScopeLabel(t, std, subjectCatalog.nameOf);
        const visibleQuestions = std.questions.filter((q) => visibleKeys.has(q.key));
        const total = visibleQuestions.length;
        const points = visibleQuestions.filter((q) => {
          const selected = answers[q.key];
          if (!selected) return false;
          const opt = optionsFor(q).find((o) => o.uuid === selected);
          return opt?.is_positive;
        }).length;

        return (
          <div
            key={std.key || std.uuid || stdIdx}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/30">
              <div className="flex justify-between items-center">
                <h2 className="text-sm font-bold text-slate-800">
                  {t("observation.domainTitle", {
                    number: std.number,
                    title:
                      displayText(std.title, lang) ||
                      t("templates.standardN", { n: std.number }),
                  })}
                </h2>
                {scopeLabel && (
                  <span className="ml-3 max-w-[50%] truncate px-2 py-0.5 rounded-md bg-indigo-50 text-[11px] font-semibold text-indigo-700">
                    {scopeLabel}
                  </span>
                )}
              </div>
              <div className="mt-2 flex items-center gap-4">
                <div className="h-1.5 flex-1 bg-slate-100 rounded-full overflow-hidden flex">
                  {visibleQuestions.map((q) => {
                    const opt = optionsFor(q).find((o) => o.uuid === answers[q.key]);
                    return (
                      <div
                        key={q.key}
                        className="h-full border-r border-white/20 last:border-none transition-all duration-300"
                        style={{
                          width: `${100 / Math.max(total, 1)}%`,
                          backgroundColor: opt?.color || "transparent",
                        }}
                      />
                    );
                  })}
                </div>
                <span className="text-[10px] font-black uppercase tracking-wider whitespace-nowrap shrink-0 text-slate-500">
                  {t("observation.points", { points, total })}
                </span>
              </div>
            </div>

            <div className="divide-y divide-slate-100">
              {visibleQuestions.map((q, qIdx) => {
                const depth = depths[q.key] || 0;
                const hiddenFollowUps = (dependentsByKey[q.key] || []).filter(
                  (k) => !visibleKeys.has(k),
                ).length;
                return (
                  <div
                    key={q.key}
                    style={depth ? { paddingLeft: 16 + depth * 24 } : undefined}
                    className={`px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-3 ${
                      depth ? "border-l-[3px] border-indigo-200 bg-indigo-50/30" : ""
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        {depth > 0 && (
                          <svg
                            className="w-3 h-3 text-indigo-400 shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2.5"
                              d="M9 5l-5 5m0 0l5 5m-5-5h11a4 4 0 014 4v2"
                            />
                          </svg>
                        )}
                        <span className="text-[9px] font-bold text-slate-500 uppercase">
                          {t("observation.questionNum", { std: std.number, q: qIdx + 1 })}
                        </span>
                        {q.isRequired && (
                          <span className="text-[8px] font-black bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded uppercase tracking-tighter">
                            {t("observation.required")}
                          </span>
                        )}
                        <HiddenFollowUpsBadge
                          count={hiddenFollowUps}
                          labelKey="templates.hiddenFollowUps"
                          hintKey="templates.hiddenFollowUpsHint"
                        />
                      </div>
                      <p className="text-sm font-medium leading-tight text-slate-700">
                        {displayText(q.text, lang) || t("templates.questionPlaceholder")}
                      </p>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                      {optionsFor(q).map((opt) => {
                        const isActive = answers[q.key] === opt.uuid;
                        return (
                          <button
                            key={opt.uuid}
                            type="button"
                            onClick={() =>
                              setAnswers((prev) => ({
                                ...prev,
                                [q.key]: isActive ? undefined : opt.uuid,
                              }))
                            }
                            style={
                              isActive
                                ? { backgroundColor: opt.color, borderColor: opt.color }
                                : undefined
                            }
                            className={`px-3 py-1.5 rounded-lg border text-[11px] font-bold transition-all ${
                              isActive
                                ? "text-white shadow-sm"
                                : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                            }`}
                          >
                            {opt.short_label || opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
